import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SuiteIcon } from '@shared/icons';
import { useMobileTabs, MORE_TAB } from './mobileTabs';
import { useMobileShell } from '@shared/shell';
import { useKeyboardOpen } from '@shared/hooks/use-keyboard';
import { useActiveSuite } from '../useActiveSuite';
import { triggerHaptic } from '@shared/lib/haptics';
import { cn } from '@shared/lib/utils';

export const MobileBottomNav: React.FC = () => {
  const tabs = useMobileTabs();
  const location = useLocation();
  const activeSuite = useActiveSuite();
  const { openLauncher } = useMobileShell();
  const isKeyboardOpen = useKeyboardOpen();

  if (isKeyboardOpen) {
    return null;
  }

  const pathname = location.pathname;

  return (
    <nav
      data-app-chrome
      aria-label="Primary"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 h-[var(--mobile-nav-total)] pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)] border-t border-border bg-card/95 supports-[backdrop-filter]:bg-card/90 supports-[backdrop-filter]:backdrop-blur-lg grid grid-cols-5 short:h-11 shadow-lg"
    >
      {tabs.map((tab) => {
        const isMore = tab.id === MORE_TAB.id;

        if (isMore) {
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                triggerHaptic(8);
                openLauncher();
              }}
              className="pressable flex flex-col items-center justify-center gap-0.5 min-h-12 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="More navigation and tools"
            >
              <SuiteIcon name={tab.icon} className="h-5 w-5 shrink-0" />
              <span className="text-[11px] font-semibold short:hidden truncate max-w-[64px]">
                {tab.label}
              </span>
            </button>
          );
        }

        const isExactMatch =
          pathname === tab.path || pathname.startsWith(tab.path + '/');
        const isSuiteMatch =
          Boolean(activeSuite?.id && activeSuite.id === tab.suite) &&
          !tabs.some((t) => t.id !== tab.id && pathname.startsWith(t.path));
        const isActive = isExactMatch || isSuiteMatch;

        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            onClick={() => triggerHaptic(8)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'pressable flex flex-col items-center justify-center gap-0.5 min-h-12 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              isActive
                ? 'text-primary font-bold'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <SuiteIcon
              name={tab.icon}
              className={cn(
                'h-5 w-5 shrink-0 transition-transform',
                isActive && 'scale-110 text-primary'
              )}
            />
            <span className="text-[11px] font-semibold short:hidden truncate max-w-[64px]">
              {tab.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
};
